<template>
    <!-- A team member's "Suggest an Improvement", drafted into the process by the Forge (API routes/ai.ts /suggestion-draft):
         compare, then save it as a draft for approval. Opened from the Forge page (AIHub "From your team") and Review. -->
    <div class="error-msg-wrap">
        <section class="form-wrapper slide-up fsd" role="dialog" aria-label="Team suggestion in the Forge">
            <button type="button" class="fsd-close" title="Close" :disabled="busy" @click="$emit('close')">×</button>
            <h3>Suggestion in the Forge</h3>
            <div v-if="view" class="fsd-quote">
                <div class="fsd-meta">From <b>{{ view.user || 'a team member' }}</b><template v-if="view.date"> · {{ dateLabel(view.date) }}</template> · on
                    <router-link :to="'/playbook/' + view.slug" target="_blank">{{ view.nodeName }}</router-link></div>
                <p>{{ view.suggestion }}</p>
            </div>

            <div v-if="phase == 'loading'" class="fsd-loading" role="status"><div class="fsd-orb"></div>{{ loadingText }}</div>
            <div v-if="error" class="fsd-error" role="alert">{{ error }}</div>

            <template v-if="phase == 'review' && view && view.draft">
                <div v-if="view.stale" class="fsd-warn">The process has changed since the Forge drafted this. Draft again to work from the current version.</div>
                <div v-else-if="view.draftPending" class="fsd-warn">This process already has a draft waiting for approval. Saving puts this change on top of it.</div>
                <div v-if="nameChanged" class="fsd-hint">Suggested new name: "{{ view.draft.name }}" <label class="fsd-check"><input type="checkbox" v-model="keepName" /> keep "{{ view.nodeName }}"</label></div>
                <forge-compare :current="view.current" :draft="view.draft.items" :changes="view.draft.changesSummary" draft-label="With the suggestion" changes-label="What changes" />
                <div v-if="!view.draft.changesSummary.length" class="fsd-hint">The Forge made no changes.</div>
            </template>

            <div class="fsd-actions">
                <button type="button" class="ai-btn ai-btn-ghost" :disabled="busy" @click="dismiss()">Dismiss from the Forge</button>
                <span style="flex:1"></span>
                <button v-if="phase != 'loading'" type="button" class="ai-btn ai-btn-ghost" :disabled="busy" @click="draft()">{{ view && view.draft ? 'Draft again' : 'Draft with the Forge' }}</button>
                <button v-if="phase == 'review' && view && view.draft" type="button" class="ai-btn" :disabled="busy || !view.draft.changesSummary.length && !nameChanged" @click="save()">{{ saving ? 'Saving…' : 'Save as Draft for Approval' }}</button>
            </div>
        </section>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import ForgeCompare from './ForgeCompare.vue';

export default defineComponent({
    name: 'ForgeSuggestionDraft',
    components: { ForgeCompare },
    // suggestion: at least { ID }; HasDraft / ForgeStatus 'drafted' opens the saved draft instead of making a new one
    props: { suggestion: { type: Object, required: true } },
    emits: [ 'close', 'saved', 'dismissed', 'drafted' ],
    data() {
        return { phase: 'loading', view: null as any, error: '', busy: false, saving: false, keepName: false, loadingText: 'Opening…' };
    },
    computed: {
        api(): string { return `${this.$store.state.apiUrl}/api/ai`; },
        nameChanged(): boolean { return !!(this.view && this.view.draft && this.view.draft.name && this.view.draft.name != this.view.nodeName); }
    },
    async mounted() {
        const s: any = this.suggestion;
        if(s.HasDraft || s.ForgeStatus == 'drafted') await this.open();
        else await this.draft();
    },
    methods: {
        dateLabel(d: string): string { return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); },
        errorText(e: any, fallback: string): string {
            const d = e?.response?.data;
            if(e?.response?.status == 403) return 'Only Admins and Editors can do this.';
            return typeof d == 'string' && d && d.length < 400 ? d : fallback;
        },
        async open() {
            this.phase = 'loading'; this.loadingText = 'Opening the Forge\'s draft…'; this.error = '';
            try {
                this.view = (await axios.get(`${this.api}/suggestion-draft/${this.suggestion.ID}`)).data;
                this.phase = this.view.draft ? 'review' : 'input';
            } catch(e: any) {
                this.error = this.errorText(e, 'Couldn\'t open this suggestion.');
                this.phase = 'input';
            }
        },
        async draft() {
            this.busy = true; this.error = ''; this.phase = 'loading';
            this.loadingText = 'The Forge is reading the process and the suggestion… (about half a minute)';
            try {
                this.view = (await axios.post(`${this.api}/suggestion-draft/${this.suggestion.ID}`, {})).data;
                this.keepName = false;
                this.phase = 'review';
                this.$emit('drafted', this.suggestion.ID);
            } catch(e: any) {
                this.error = this.errorText(e, 'The Forge couldn\'t draft that change. Please try again.');
                this.phase = this.view && this.view.draft ? 'review' : 'input';
            }
            this.busy = false;
        },
        async save(force = false) {
            if(this.busy) return;
            const items = (this.view && this.view.draft && this.view.draft.items) || [];
            if(!force && items.some((i: any) => (i.body || '').includes('[confirm:')) &&
                !confirm('This version contains [confirm: ...] placeholders the process owner should fill in before it goes live. Save as draft anyway?')) return;
            this.busy = true; this.saving = true; this.error = '';
            try {
                const r = (await axios.post(`${this.api}/suggestion-draft/${this.suggestion.ID}/apply`, { force, keepName: this.keepName })).data;
                this.$emit('saved', { id: this.suggestion.ID, slug: r.slug });
            } catch(e: any) {
                if(e?.response?.status == 409 && confirm(e.response.data + ' Save anyway?')) {
                    this.busy = false; this.saving = false;
                    await this.save(true);
                    return;
                }
                this.error = this.errorText(e, 'Could not save the draft.');
            }
            this.busy = false; this.saving = false;
        },
        async dismiss() {
            this.busy = true; this.error = '';
            try {
                await axios.post(`${this.api}/team-suggestions/${this.suggestion.ID}/dismiss`, {});
                this.$emit('dismissed', this.suggestion.ID);
            } catch(e: any) {
                this.error = this.errorText(e, 'Couldn\'t dismiss it.');
            }
            this.busy = false;
        }
    }
});
</script>

<style scoped>
.fsd { position: relative; width: 960px; max-width: calc(100vw - 32px); max-height: calc(100vh - 40px); overflow: auto; text-align: left; }
.fsd h3 { margin: 0 0 10px; }
.fsd-close { position: absolute; top: 10px; right: 12px; border: none; background: none; font-size: 24px; line-height: 1; cursor: pointer; color: #8a7f75; }
.fsd-quote { background: var(--ai-grad-soft); border: 1px solid var(--ai-border-soft); border-radius: 10px; padding: 10px 14px; margin-bottom: 12px; }
.fsd-quote p { margin: 6px 0 0; white-space: pre-wrap; font-size: 14px; color: #2b221b; }
.fsd-meta { font-size: 12.5px; color: #6f655c; }
.fsd-loading { display: flex; align-items: center; gap: 10px; font-size: 14px; color: #6f655c; padding: 18px 0; }
.fsd-orb { width: 18px; height: 18px; border-radius: 50%; background: var(--ai-grad); animation: fsd-pulse 1.2s ease-in-out infinite; }
@keyframes fsd-pulse { 0%, 100% { opacity: .4; transform: scale(.85); } 50% { opacity: 1; transform: scale(1); } }
.fsd-error { color: #a52c26; font-size: 14px; margin: 8px 0; }
.fsd-warn { background: #fff3cd; border: 1px solid #ffe08a; color: #6b5300; border-radius: 8px; padding: 8px 12px; font-size: 13.5px; margin-bottom: 10px; }
.fsd-hint { font-size: 13px; color: #6f655c; margin: 6px 0 10px; }
.fsd-check { margin-left: 8px; font-weight: 600; }
.fsd-actions { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-top: 16px; }
@media (max-width: 640px) { .fsd-actions .ai-btn { flex: 1 1 auto; justify-content: center; } }
</style>
